import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Section, Eyebrow, Tag } from "../components/ui";
import Constellation from "../components/Constellation";
import CapabilitySpectrum from "../components/CapabilitySpectrum";
import CombinationDiagram from "../components/CombinationDiagram";
import { howIWork } from "../data/experience";
import lcrSummitCover from "../assets/work/lcr-summit-cover.webp";
import sabrinaHero from "../assets/work/sabrina-hero.webp";

const waysIn = [
  {
    t: "Build week",
    d: "A focused sprint that turns a vague idea into a working, demonstrable product. Sabrina went from idea to a working adaptive engine in an afternoon. England's entire national curriculum got mapped into one navigable system in a day. A national broadcaster got a full product and engineering strategy, plus a working platform, in a month.",
  },
  {
    t: "Strategy & consultancy",
    d: "Product and technology strategy for boards and executive teams, grounded in what will actually ship, not a deck that sits on a shelf.",
  },
  {
    t: "Embedded leadership",
    d: "Fractional CTO or CPTO, or a team lead inside the organisation: setting direction, running the team, and still writing code.",
  },
  {
    t: "Service design & research",
    d: "User research, jobs-to-be-done, and service design that find the real problem before anything gets built.",
  },
  {
    t: "Policy & operating model",
    d: "Advisory at the policy or operating-model level, for organisations rethinking how delivery works once AI changes what a small team can do.",
  },
];

const aiProofPoints = [
  {
    t: "Discovery ends with a build",
    d: "A strategy discovery that used to take six months now takes a week. It doesn't stop at a recommendation. It ends with a production build people can actually use.",
    href: "/work",
    cta: "See the work",
  },
  {
    t: "The team is still the unit of delivery",
    d: "AI is ubiquitous now. Everyone has access to it. What matters is how you use it, and I have an opinionated view on that workflow: every decision stays with the humans in the room. AI doesn't replace the team. It multiplies what the team gets through in a day.",
  },
  {
    t: "The speed is fifteen years old",
    d: "A build that would have taken twelve weeks five years ago now takes a day. The decisions already exist: fifteen years of pattern-recognition across startups and national-scale delivery. AI removed the cost of acting on them.",
    href: "/cv",
    cta: "Read the full CV",
  },
];

const highlights = [
  {
    title: "LCR Summit",
    tag: "Data visualisation",
    description:
      "The English curriculum rendered as one navigable network: every statement sequenced and bridged.",
    image: lcrSummitCover,
    href: "/work/ai-data#lcr-summit",
  },
  {
    title: "NHS Validated Relationships",
    tag: "National platform",
    description: "A serverless, event-driven service supporting 700k+ proxy role migrations across the NHS.",
    href: "/work/client-delivery#nhs-validated-relationships-service",
  },
  {
    title: "Sabrina",
    tag: "Adaptive learning",
    description: "Spelling practice that finds the rule a child is stuck on. Idea to working product: a few hours.",
    image: sabrinaHero,
    href: "/work/founder-ventures#sabrina",
  },
  {
    title: "Gamstop National Self-Exclusion Platform",
    tag: "National infrastructure",
    description:
      "Consolidated 8 siloed platforms into one national portal connecting every UK gambling operator, protecting vulnerable users at scale.",
    href: "/work/client-delivery#gamstop-national-self-exclusion-platform",
  },
];

const Home = () => (
  <main>
    <Section className="relative overflow-hidden !pb-8 sm:!pb-10">
      <Constellation className="pointer-events-none absolute -right-[22rem] -top-56 hidden h-[58rem] w-[58rem] opacity-80 md:block lg:-right-72" />
      <div className="relative max-w-2xl">
        <Eyebrow>Simon Stead · Technology & AI Strategy · CPTO</Eyebrow>
        <h1 className="text-5xl font-black leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
          Code is cheap now.
          <br />
          Ideas, pace, and strategy aren't.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-ink-soft">
          Most people in this work pick a lane: strategy or delivery, research or engineering,
          startup speed or enterprise scale. I've spent a decade running all of them at once,
          trusted by boards and chief executives at the Co-op, the BBC, Lloyds, and the NHS.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/work"
            className="rounded-full bg-ink px-6 py-3 text-base font-semibold text-paper transition-colors hover:bg-accent-dark"
          >
            See the work
          </Link>
          <a
            href="mailto:steady@steadtec.com"
            className="rounded-full border border-ink px-6 py-3 text-base font-semibold text-ink transition-colors hover:border-accent hover:text-accent"
          >
            Get in touch
          </a>
        </div>
      </div>
    </Section>

    <Section tone="panel">
      <Eyebrow>The combination</Eyebrow>
      <h2 className="max-w-2xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
        Strategy, research, and engineering, at any scale from solo to enterprise.
      </h2>
      <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div className="max-w-prose space-y-5 text-lg leading-relaxed text-ink-soft">
          <p>
            User research and service design find the problem. Extreme programming and
            test-driven development keep the build honest. Event-sourced architecture and
            production systems hold it at national scale. A founder's instinct for speed, built
            across five live ventures, sits underneath all of it.
          </p>
          <p>
            Agentic AI is the newest part of that practice. It changes the pace. It doesn't
            change the rigour. This isn't vibe coding: user research still finds the problem,
            architecture still holds under load, and I build the AI tooling myself.
          </p>
        </div>
        <CombinationDiagram />
      </div>
    </Section>

    <Section>
      <div className="relative">
        <Eyebrow>Range</Eyebrow>
        <h2 className="max-w-2xl text-4xl font-bold leading-tight text-ink sm:text-5xl">
          Strategy at the top. Algebra at the bottom. I work the whole stack.
        </h2>
        <div className="mt-14 max-w-4xl">
          <CapabilitySpectrum />
        </div>
      </div>
    </Section>

    <Section tone="panel">
      <Eyebrow>Ways in</Eyebrow>
      <h2 className="max-w-2xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
        I think most organisations have about a year to work out where they stand, once AI
        changes what a small team can build.
      </h2>
      <p className="mt-4 max-w-2xl text-ink-soft">
        Getting someone who has already done this, at every altitude, shortens that window.
        Here's what that can look like.
      </p>
      <div className="mt-12 grid gap-10 sm:grid-cols-2">
        {waysIn.map((w) => (
          <div key={w.t}>
            <h3 className="text-lg font-bold text-ink">{w.t}</h3>
            <p className="mt-2 text-ink-soft">{w.d}</p>
          </div>
        ))}
      </div>
      <a
        href="mailto:steady@steadtec.com"
        className="mt-10 inline-flex items-center gap-1 rounded-full bg-ink px-6 py-3 text-base font-semibold text-paper transition-colors hover:bg-accent-dark"
      >
        Get in touch
      </a>
    </Section>

    <Section>
      <Eyebrow>Where AI fits</Eyebrow>
      <h2 className="max-w-3xl text-4xl font-bold leading-tight text-ink sm:text-5xl">
        A six-month strategy discovery now takes a week, and it ends with a working build.
      </h2>
      <div className="mt-12 grid gap-10 sm:grid-cols-3">
        {aiProofPoints.map((p) => (
          <div key={p.t}>
            <h3 className="text-lg font-semibold text-ink">{p.t}</h3>
            <p className="mt-3 text-ink-soft">{p.d}</p>
            {p.href && (
              <a
                href={p.href}
                target={p.href.startsWith("http") ? "_blank" : undefined}
                rel={p.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-accent-dark"
              >
                {p.cta} <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </div>
        ))}
      </div>
      <p className="mt-14 max-w-2xl border-t border-line pt-10 text-xl font-bold leading-snug text-ink">
        I think the next decade of public services, in the UK and beyond, gets rebuilt at this
        pace, by people who still put humans in the room for every decision. That's the work I
        want.
      </p>
    </Section>

    <Section tone="panel">
      <Eyebrow>How I work</Eyebrow>
      <div className="max-w-prose space-y-5 text-lg leading-relaxed text-ink-soft">
        {howIWork.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </Section>

    <Section>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Selected work</Eyebrow>
          <h2 className="text-4xl font-bold text-ink sm:text-5xl">A few places to start</h2>
        </div>
        <Link to="/work" className="text-sm font-medium text-accent hover:text-accent-dark">
          View all work →
        </Link>
      </div>
      <div className="mt-10 grid gap-8 sm:grid-cols-2">
        {highlights.map((h) => (
          <Link
            key={h.title}
            to={h.href}
            className="group block overflow-hidden rounded-lg border border-line bg-paper transition-colors hover:border-accent"
          >
            {h.image && (
              <img
                src={h.image}
                alt=""
                loading="lazy"
                className="h-56 w-full object-cover object-top"
              />
            )}
            <div className="p-6">
              <Tag>{h.tag}</Tag>
              <h3 className="mt-3 text-xl font-semibold text-ink group-hover:text-accent">
                {h.title}
              </h3>
              <p className="mt-2 text-ink-soft">{h.description}</p>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  </main>
);

export default Home;
